import React, { useEffect, useState, useMemo } from "react";
import { db } from "../../../firebase";
import { collection, onSnapshot, doc, deleteDoc, updateDoc, setDoc, query, getDocs, serverTimestamp } from "firebase/firestore";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ESTADOS_EMPRESA, obtenerDescripcionEstado, puedeTransicionar, normalizarTipoEmpresaAlGuardar } from "../../../utils/empresaStandards";
import { MEMBERSHIP_PLANS, getPlanById, SUBSCRIPTION_STATUS, BILLING_CYCLES } from "../../../constants/membershipPlans";
import { useImageUrl } from '../../../hooks/useImageUrl';
import GenerarDatosPruebaService from '../../../services/GenerarDatosPruebaService';
import ImportarEmpresasPublicasJson from './ImportarEmpresasPublicasJson';
import * as XLSX from 'xlsx';
import { getPaginaWeb } from '../../../utils/empresaWebRedes';
import { getEmpresaMarcas } from '../../../utils/empresaMarcasHelper';
import ContactVerificationModal from '../../../features/admin/components/admin/ContactVerificationModal';
import { generateOrRegenerateCompanyPublicProfile } from '../services/companyPublicProfileService';
import { buildPublicVerificationState, normalizeEmpresaContactos } from '../../../utils/empresaContactosVerification';
import {
  empresaCumpleFiltrosListadoAdmin,
  ESTADOS_FILTRO_ADMIN,
  FILTROS_EMPRESAS_ADMIN_INITIAL,
  empresaTieneQrPublicoGeneradoAdmin as empresaTieneQrPublicoGenerado,
  empresaTieneLatitudEnDocumentoAdmin as empresaTieneLatitudEnDocumento
} from '../../../utils/empresasAdminFilter';

function getImagePath(value) {
  if (!value) return "";
  if (value.startsWith("http")) return value;
  if (value.startsWith("gs://")) return value; // Firebase Storage URLs will be handled by useImageUrl hook
  return `/images/${value}`;
}

function normalizeDuplicateValue(value) {
  if (value == null) return '';
  return String(value)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
}

function normalizePhoneForDuplicate(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 8) return `569${digits}`;
  if (digits.startsWith('56')) return digits;
  return digits;
}

function normalizeWebForDuplicate(value) {
  const v = normalizeDuplicateValue(value)
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/+$/, '');
  return v;
}

/** Serializa servicios (array de strings u objetos) para Excel */
function serviciosToCell(servicios) {
  if (!servicios || !Array.isArray(servicios)) return '';
  return servicios
    .map((s) => (typeof s === 'string' ? s : s?.nombre || s?.id || JSON.stringify(s)))
    .filter(Boolean)
    .join(', ');
}

/** Serializa marcas (array de strings u objetos) para Excel */
function marcasToCell(marcas) {
  if (!marcas || !Array.isArray(marcas)) return '';
  return marcas
    .map((m) => (typeof m === 'string' ? m : m?.nombre || m?.id || JSON.stringify(m)))
    .filter(Boolean)
    .join(', ');
}

function formatDateCell(value) {
  if (!value) return '';
  try {
    const d = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString();
  } catch (_) {
    return '';
  }
}

/** Exporta empresas con estado activa a Excel (incl. tipo empresa; columnas: nombre, direccion, servicios, marcas, estado, tipo empresa, pagina web, mail) */
function exportEmpresasActivasExcel(empresasList) {
  const activas = empresasList.filter((e) => e.estado === ESTADOS_EMPRESA.ACTIVA);
  const rows = activas.map((e) => ({
    Nombre: e.nombre || '',
    Region: e.region || '',
    Comuna: e.comuna || '',
    Direccion: e.direccion || e.direccion_empresa || '',
    Servicios: serviciosToCell(e.servicios),
    Marca: marcasToCell(getEmpresaMarcas(e) || e.marcas),
    activado_por: e.activado_por || '',
    perfilPublicadoPor: e.perfilPublicadoPor || e.perfil_publicado_por || '',
    'tipo empresa': e.tipoEmpresa || e.tipo_empresa || '',
    web: getPaginaWeb(e) || e.web || e.pagina_web || '',
    Instagram: e.instagram || e?.redes_sociales?.instagram || '',
    email: normalizeEmpresaContactos(e)?.correo?.valor || e.email || e.email_empresa || '',
    'telefono fijo': normalizeEmpresaContactos(e)?.telefonoFijo?.valor || e.telefonoFijo || e.telefono_fijo || '',
    wsp: normalizeEmpresaContactos(e)?.whatsapp?.valor || e.whatsapp || e.wsap || e.telefono || '',
    verificado: buildPublicVerificationState(e) ? 'si' : 'no',
    logo: e.logo_url || e.logo || e.logoUrl || '',
    fecha_activacion: formatDateCell(e.fecha_activacion || e.fechaActivacion || e.activatedAt || e.fecha_activacion_ts)
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Empresas activas');
  const fecha = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `empresas-activas-${fecha}.xlsx`);
}

function colorEstado(estado) {
  if (!estado) return "bg-gray-200 text-gray-700";
  
  // Normalizar el estado para comparación
  const estadoNormalizado = estado.toLowerCase();
  
  switch (estadoNormalizado) {
    case "activa":
    case "activo":
      return "bg-green-100 text-green-700";
    case "validada":
      return "bg-blue-100 text-blue-700";
    case "pendiente_validacion":
    case "pendiente de validación":
      return "bg-yellow-100 text-yellow-700";
    case "en_visita":
    case "en visita":
      return "bg-purple-100 text-purple-700";
    case "catalogada":
      return "bg-indigo-100 text-indigo-700";
    case "ingresada":
      return "bg-teal-100 text-teal-700";
    case "inactiva":
    case "inactivo":
      return "bg-red-200 text-red-800";
    case "suspendida":
    case "suspendido":
      return "bg-orange-200 text-orange-800";
    case "rechazada":
      return "bg-red-300 text-red-900";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

/** Solo empresas marcadas inactivas pueden borrarse desde este listado (evita borrados accidentales). */
function estadoEsInactiva(estado) {
  const n = String(estado ?? '').trim().toLowerCase();
  return n === 'inactiva' || n === 'inactivo';
}

function estadoEsIngresada(estado) {
  return String(estado ?? '').trim().toLowerCase() === ESTADOS_EMPRESA.INGRESADA;
}

/** Inactiva (definitivo) o Ingresada (descartar registro manual duplicado). */
function puedeEliminarDesdeListado(estado) {
  return estadoEsInactiva(estado) || estadoEsIngresada(estado);
}

function normalizeEstadoKey(raw) {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function empresaEsActivaParaQr(empresa) {
  const e = normalizeEstadoKey(empresa?.estado);
  return e === 'activa' || e === 'activo' || e === 'validada' || e === 'validado';
}

// Componente para mostrar logo con manejo de URLs de Firebase Storage
function LogoImage({ logo, nombre, className = "h-12 w-12 rounded object-cover" }) {
  const { imageUrl, loading, error } = useImageUrl(logo);

  if (!logo) {
    return (
      <div className={`${className} bg-gray-200 flex items-center justify-center`}>
        <span className="text-xs text-gray-500">Sin logo</span>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`${className} bg-gray-200 flex items-center justify-center`}>
        <div className="w-4 h-4 border-2 border-gray-400 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !imageUrl) {
    return (
      <div className={`${className} bg-gray-200 flex items-center justify-center`}>
        <span className="text-xs text-red-500">Error</span>
      </div>
    );
  }

  return (
    <div className="relative h-12 w-12 flex-shrink-0 flex items-center justify-center rounded overflow-hidden bg-gray-200">
      <img 
        className={className} 
        src={imageUrl} 
        alt={`Logo de ${nombre}`}
        onError={(e) => {
          e.target.style.display = 'none';
          const fallback = e.target.nextElementSibling;
          if (fallback) fallback.style.display = 'flex';
        }}
      />
      <div className="absolute inset-0 flex items-center justify-center bg-gray-200" style={{ display: 'none' }}>
        <span className="text-xs text-gray-500">{nombre ? nombre.charAt(0).toUpperCase() : '?'}</span>
      </div>
    </div>
  );
}

export default function GestionEmpresas() {
  const [empresas, setEmpresas] = useState([]);
  const [adminNameByEmail, setAdminNameByEmail] = useState({});
  const [activadoPorFiltro, setActivadoPorFiltro] = useState('');
  const [filtros, setFiltros] = useState(() => ({ ...FILTROS_EMPRESAS_ADMIN_INITIAL }));
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table');
  const [empresaVistaPrevia, setEmpresaVistaPrevia] = useState(null);
  const [empresasSeleccionadasIds, setEmpresasSeleccionadasIds] = useState([]);
  /** Edición rápida de nombre en tabla (clic en nombre) */
  const [nombreRapidoEditId, setNombreRapidoEditId] = useState(null);
  const [nombreRapidoValor, setNombreRapidoValor] = useState('');
  const [nombreRapidoGuardando, setNombreRapidoGuardando] = useState(false);
  const [marcaMultimarcaGuardandoId, setMarcaMultimarcaGuardandoId] = useState(null);
  const [generandoDatos, setGenerandoDatos] = useState(null); // ID de la empresa para la que se están generando datos
  const [pasarAPremiumLoading, setPasarAPremiumLoading] = useState(null); // empresaId en proceso
  const [pasarAEnterpriseLoading, setPasarAEnterpriseLoading] = useState(null);
  const [visitasPorEmpresa, setContactosPorEmpresa] = useState({}); // { empresaId: count } — contactos_empresa (incl. WhatsApp / formulario)
  const [visitasPerfilPorEmpresa, setVisitasPerfilPorEmpresa] = useState({}); // { empresaId: count } - visitas al perfil (visitas_perfil_empresa)
  const [lastSessionByEmpresa, setLastSessionByEmpresa] = useState({}); // { empresaId: { lastActiveAt, lastLoginAt } }
  const [cargandoSesionesYVisitas, setCargandoSesionesYVisitas] = useState(true);
  const [mostrarImportarJson, setMostrarImportarJson] = useState(false);
  const [eliminandoEmpresaId, setEliminandoEmpresaId] = useState(null);
  const [contactVerifyEmpresa, setContactVerifyEmpresa] = useState(null);
  const [bulkQrState, setBulkQrState] = useState({
    running: false,
    total: 0,
    done: 0,
    ok: 0,
    fail: 0,
    current: '',
    lastError: '',
  });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isDuplicateView = (searchParams.get('vista') || '').toLowerCase() === 'repetidas';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'usuarios'), where('rol', '==', 'admin')));
        if (cancelled) return;
        const out = {};
        snap.docs.forEach((d) => {
          const data = d.data() || {};
          const email = String(data.email || '').trim().toLowerCase();
          const nombre = String(data.nombre || data.displayName || '').trim();
          if (email) out[email] = nombre || data.email || email;
        });
        setAdminNameByEmail(out);
      } catch (e) {
        console.warn('No se pudo cargar usuarios admin para tooltip activación:', e);
        if (!cancelled) setAdminNameByEmail({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const getAdminDisplayNameFromEmail = (email) => {
    const key = String(email || '').trim().toLowerCase();
    if (!key) return '';
    return adminNameByEmail[key] || String(email || '').trim();
  };

  const activadoresDisponibles = useMemo(() => {
    const seen = new Set();
    empresas.forEach((e) => {
      const v = String(e?.activado_por || '').trim();
      if (!v) return;
      seen.add(v.toLowerCase());
    });
    return [...seen];
  }, [empresas]);

  const MINUTOS_ONLINE = 15;
  const toMs = (v) => {
    if (!v) return null;
    if (v && typeof v.toDate === 'function') return v.toDate().getTime();
    if (typeof v === 'number') return v;
    try { return new Date(v).getTime(); } catch (_) { return null; }
  };
  const isPerfilOnline = (empresaId) => {
    const s = lastSessionByEmpresa[empresaId];
    const lastMs = s ? (toMs(s.lastActiveAt) || toMs(s.lastLoginAt)) : null;
    if (!lastMs) return false;
    return Date.now() - lastMs < MINUTOS_ONLINE * 60 * 1000;
  };
  const formatLastSession = (empresaId) => {
    const s = lastSessionByEmpresa[empresaId];
    const lastMs = s ? (toMs(s.lastActiveAt) || toMs(s.lastLoginAt)) : null;
    if (!lastMs) return '—';
    const diff = Date.now() - lastMs;
    if (diff < 60 * 1000) return 'Ahora';
    if (diff < 60 * 60 * 1000) return `Hace ${Math.floor(diff / 60000)} min`;
    if (diff < 24 * 60 * 60 * 1000) return `Hace ${Math.floor(diff / 3600000)} h`;
    const d = new Date(lastMs);
    return d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const iniciarEdicionNombreRapido = (empresa) => {
    setNombreRapidoEditId(empresa.id);
    setNombreRapidoValor(empresa.nombre || '');
  };

  const cancelarEdicionNombreRapido = () => {
    setNombreRapidoEditId(null);
    setNombreRapidoValor('');
  };

  const guardarNombreRapido = async () => {
    const id = nombreRapidoEditId;
    if (!id) return;
    const v = nombreRapidoValor.trim();
    if (!v) {
      alert('El nombre no puede quedar vacío.');
      return;
    }
    setNombreRapidoGuardando(true);
    try {
      await updateDoc(doc(db, 'empresas', id), {
        nombre: v,
        fecha_actualizacion: serverTimestamp(),
      });
      cancelarEdicionNombreRapido();
    } catch (err) {
      console.error('Guardar nombre rápido:', err);
      alert('No se pudo guardar el nombre. Revisa permisos o conexión.');
    } finally {
      setNombreRapidoGuardando(false);
    }
  };

  const asignarMarcaMultimarca = async (empresaId) => {
    setMarcaMultimarcaGuardandoId(empresaId);
    try {
      await updateDoc(doc(db, 'empresas', empresaId), {
        marcas: ['Multimarca'],
        fecha_actualizacion: serverTimestamp(),
      });
    } catch (err) {
      console.error('Asignar Multimarca:', err);
      alert('No se pudo asignar la marca. Revisa permisos o conexión.');
    } finally {
      setMarcaMultimarcaGuardandoId(null);
    }
  };

  // Contactos, visitas al perfil y última sesión (indicadores en filas del listado)
  useEffect(() => {
    let cancelled = false;
    const cargar = async () => {
      setCargandoSesionesYVisitas(true);
      try {
        const [contactosSnap, visitasSnap, usuariosSnap] = await Promise.all([
          getDocs(collection(db, 'contactos_empresa')),
          getDocs(collection(db, 'visitas_perfil_empresa')),
          getDocs(collection(db, 'usuarios')),
        ]);
        if (cancelled) return;

        const countsContactos = {};
        contactosSnap.docs.forEach((d) => {
          const empresaId = d.data().empresaId;
          if (empresaId) countsContactos[empresaId] = (countsContactos[empresaId] || 0) + 1;
        });
        setContactosPorEmpresa(countsContactos);

        const countsPerfil = {};
        visitasSnap.docs.forEach((d) => {
          const empresaId = d.data().empresaId;
          if (empresaId) countsPerfil[empresaId] = (countsPerfil[empresaId] || 0) + 1;
        });
        setVisitasPerfilPorEmpresa(countsPerfil);

        const byEmpresa = {};
        usuariosSnap.docs.forEach((d) => {
          const data = d.data();
          const eid = data.empresaId || data.empresa_id;
          if (!eid) return;
          const lastActiveAt = data.lastActiveAt || null;
          const lastLoginAt = data.lastLoginAt || null;
          const prev = byEmpresa[eid];
          const prevMs = prev ? (toMs(prev.lastActiveAt) || toMs(prev.lastLoginAt)) : 0;
          const currMs = toMs(lastActiveAt) || toMs(lastLoginAt) || 0;
          if (currMs >= prevMs) byEmpresa[eid] = { lastActiveAt, lastLoginAt };
        });
        setLastSessionByEmpresa(byEmpresa);
      } catch (err) {
        console.error('Error cargando visitas/sesiones (listado empresas):', err);
      } finally {
        if (!cancelled) setCargandoSesionesYVisitas(false);
      }
    };
    cargar();
    return () => {
      cancelled = true;
    };
  }, []);

  const getServiciosPreview = (empresa) => {
    const servicios = Array.isArray(empresa.servicios) ? empresa.servicios : [];
    if (!servicios.length) return [];
    const normalizados = servicios
      .map((s) => (typeof s === 'string' ? s : s?.nombre || s?.id || ''))
      .filter(Boolean);
    return normalizados;
  };

  const getMarcasPreview = (empresa) => {
    const marcas = getEmpresaMarcas(empresa);
    if (!marcas.length) return [];
    return marcas.map((m) => (typeof m === 'string' ? m : m?.nombre || m?.id || String(m))).filter(Boolean);
  };

  useEffect(() => {
    console.log('🔍 Iniciando carga de empresas...');
    console.log('🔥 Firebase config:', db);
    
    setLoading(true);
    
    try {
      // Consulta simple sin orderBy para evitar errores de índice
      const q = query(collection(db, "empresas"));
      
      console.log('📡 Configurando listener...');
      const unsub = onSnapshot(q, 
        (snapshot) => {
          console.log('✅ Snapshot recibido');
          console.log('📊 Número de documentos:', snapshot.docs.length);
          console.log('📄 Metadata:', snapshot.metadata);
          
          if (snapshot.empty) {
            console.log('⚠️ La colección está vacía');
          }
          
          const empresasData = snapshot.docs.map(doc => {
            const data = doc.data();
            console.log('� Procesando empresa:', doc.id, data);
            return { 
              id: doc.id, 
              ...data,
              // Asegurar que tenemos campos básicos
              nombre: data.nombre || 'Sin nombre',
              estado: data.estado || 'inactiva',
              categoria: data.categoria || 'Sin categoría'
            };
          });
          
          console.log('✨ Empresas procesadas:', empresasData);
          setEmpresas(empresasData);
          setLoading(false);
        },
        (error) => {
          console.error('❌ Error en snapshot listener:', error);
          console.error('📝 Detalles del error:', error.code, error.message);
          setLoading(false);
          
          // Intentar mostrar un mensaje más específico
          if (error.code === 'permission-denied') {
            console.error('🚫 Sin permisos para leer la colección empresas');
          } else if (error.code === 'unavailable') {
            console.error('🌐 Firebase no disponible - problema de conexión');
          }
        }
      );
      
      return unsub;
    } catch (error) {
      console.error('💥 Error configurando listener:', error);
      setLoading(false);
    }
  }, []);

  const empresaById = useMemo(() => {
    const m = new Map();
    empresas.forEach((e) => {
      if (e?.id) m.set(e.id, e);
    });
    return m;
  }, [empresas]);

  const empresasSeleccionadasIngresadasIds = useMemo(() => {
    return (empresasSeleccionadasIds || []).filter((id) => estadoEsIngresada(empresaById.get(id)?.estado));
  }, [empresasSeleccionadasIds, empresaById]);

  useEffect(() => {
    // Nunca mantener seleccionadas empresas que ya no estén en estado "ingresada" (o ya no existan).
    setEmpresasSeleccionadasIds((prev) => {
      const curr = Array.isArray(prev) ? prev : [];
      const filtered = curr.filter((id) => estadoEsIngresada(empresaById.get(id)?.estado));
      return filtered.length === curr.length ? curr : filtered;
    });
  }, [empresaById]);

  const toggleEmpresaSeleccionada = (empresa, checked) => {
    if (!empresa?.id) return;
    if (!estadoEsIngresada(empresa.estado)) return; // Nunca permitir selección fuera de "ingresada"
    setEmpresasSeleccionadasIds((prev) => {
      const curr = Array.isArray(prev) ? prev : [];
      const has = curr.includes(empresa.id);
      if (checked && !has) return [...curr, empresa.id];
      if (!checked && has) return curr.filter((x) => x !== empresa.id);
      return curr;
    });
  };

  const limpiarSeleccionEmpresas = () => setEmpresasSeleccionadasIds([]);

  const eliminarEmpresasSeleccionadasIngresadas = async () => {
    const ids = empresasSeleccionadasIngresadasIds;
    if (!ids.length) return false;

    if (
      !window.confirm(
        `¿Eliminar definitivamente ${ids.length} empresa${ids.length !== 1 ? 's' : ''} seleccionada${ids.length !== 1 ? 's' : ''}?\n\n` +
          `Solo se eliminarán empresas en estado "ingresada". Esta acción no se puede deshacer.`
      )
    ) {
      return true; // hubo selección, pero el usuario canceló: no continuar con borrado individual
    }

    setEliminandoEmpresaId('__bulk__');
    try {
      const results = await Promise.allSettled(ids.map((id) => deleteDoc(doc(db, 'empresas', id))));
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const fail = results.length - ok;

      if (empresaVistaPrevia?.id && ids.includes(empresaVistaPrevia.id)) setEmpresaVistaPrevia(null);
      if (contactVerifyEmpresa?.id && ids.includes(contactVerifyEmpresa.id)) setContactVerifyEmpresa(null);

      if (fail > 0) {
        alert(`Eliminación masiva: eliminadas ${ok} de ${results.length}. Fallaron ${fail}.`);
      } else {
        alert(`Eliminación masiva: eliminadas ${ok}.`);
      }
    } catch (error) {
      console.error('Error eliminando empresas seleccionadas:', error);
      alert('Error al eliminar empresas seleccionadas: ' + (error.message || String(error)));
    } finally {
      limpiarSeleccionEmpresas();
      setEliminandoEmpresaId(null);
    }

    return true;
  };

  const toggleEstado = async (empresa) => {
    try {
      // Determinar el nuevo estado basado en el estado actual
      let nuevoEstado;
      if (empresa.estado === ESTADOS_EMPRESA.ACTIVA) {
        nuevoEstado = ESTADOS_EMPRESA.INACTIVA;
      } else if (empresa.estado === ESTADOS_EMPRESA.INACTIVA) {
        nuevoEstado = ESTADOS_EMPRESA.ACTIVA;
      } else if (empresa.estado === ESTADOS_EMPRESA.VALIDADA) {
        nuevoEstado = ESTADOS_EMPRESA.ACTIVA;
      } else if (empresa.estado === ESTADOS_EMPRESA.CATALOGADA) {
        nuevoEstado = ESTADOS_EMPRESA.PENDIENTE_VALIDACION;
      } else if (empresa.estado === ESTADOS_EMPRESA.INGRESADA) {
        // Para empresas ingresadas, dar opción al admin
        const opcion = window.confirm(
          `Empresa: ${empresa.nombre}\n\n` +
          `Estado actual: Ingresada\n\n` +
          `¿Qué quieres hacer?\n` +
          `• OK = Activar directamente (ir a estado "Activa")\n` +
          `• Cancelar = Asignar a validación (ir a estado "Pendiente de Validación")`
        );
        
        nuevoEstado = opcion ? ESTADOS_EMPRESA.ACTIVA : ESTADOS_EMPRESA.PENDIENTE_VALIDACION;
      } else {
        nuevoEstado = ESTADOS_EMPRESA.ACTIVA;
      }

      const updatePayload = {
        estado: nuevoEstado,
        fecha_actualizacion: new Date()
      };
      // Al pasar a Activa, asegurar tipoEmpresa proveedor para que cuente en Home (salvo concesionaria)
      if (nuevoEstado === ESTADOS_EMPRESA.ACTIVA) {
        updatePayload.tipoEmpresa = normalizarTipoEmpresaAlGuardar(empresa.tipoEmpresa);
      }
      await updateDoc(doc(db, "empresas", empresa.id), updatePayload);
      
      const mensaje = nuevoEstado === ESTADOS_EMPRESA.ACTIVA 
        ? `Empresa ${empresa.nombre} activada exitosamente` 
        : `Empresa ${empresa.nombre} asignada a validación exitosamente`;
      
      alert(mensaje);
    } catch (error) {
      console.error('Error cambiando estado:', error);
      alert('Error al cambiar el estado');
    }
  };

  const suspenderEmpresa = async (empresa) => {
    if (window.confirm(`¿Estás seguro de suspender a ${empresa.nombre}?`)) {
      try {
        await updateDoc(doc(db, "empresas", empresa.id), {
          estado: ESTADOS_EMPRESA.SUSPENDIDA,
          fecha_suspension: new Date(),
          fecha_actualizacion: new Date()
        });
        alert('Empresa suspendida exitosamente');
      } catch (error) {
        console.error('Error suspendiendo empresa:', error);
        alert('Error al suspender la empresa');
      }
    }
  };

  const esPremium = (empresa) =>
    empresa?.plan === "premium" || empresa?.membresia?.plan === "premium";

  const esEnterprise = (empresa) =>
    empresa?.plan === "enterprise" || empresa?.membresia?.plan === "enterprise";

  const pasarAPremium = async (empresa) => {
    if (esPremium(empresa)) {
      alert(`${empresa.nombre} ya tiene membresía Premium.`);
      return;
    }
    if (!window.confirm(`¿Pasar a "${empresa.nombre}" a membresía Premium?\n\nSe actualizará la suscripción y el plan de la empresa.`)) {
      return;
    }
    setPasarAPremiumLoading(empresa.id);
    try {
      const newPlan = getPlanById("premium");
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 1);

      const subscriptionRef = doc(db, "subscriptions", empresa.id);
      const subscriptionData = {
        empresaId: empresa.id,
        planId: "premium",
        status: SUBSCRIPTION_STATUS.ACTIVE,
        startDate: new Date(),
        endDate,
        billingCycle: BILLING_CYCLES.MONTHLY,
        autoRenew: true,
        features: newPlan.features,
        limitations: newPlan.limitations,
        updatedAt: new Date()
      };
      await setDoc(subscriptionRef, subscriptionData, { merge: true });

      const empresaRef = doc(db, "empresas", empresa.id);
      await updateDoc(empresaRef, {
        plan: "premium",
        membresia: {
          plan: "premium",
          fechaInicio: new Date(),
          fechaVencimiento: endDate,
          estado: "activa",
          caracteristicas: newPlan.features,
          limitaciones: newPlan.limitations
        },
        nivel_suscripcion: "premium",
        fecha_actualizacion_plan: new Date(),
        fecha_actualizacion: new Date()
      });

      alert(`✅ ${empresa.nombre} pasada a membresía Premium exitosamente.`);
    } catch (error) {
      console.error("Error pasando a Premium:", error);
      alert(`Error al pasar a Premium: ${error.message}`);
    } finally {
      setPasarAPremiumLoading(null);
    }
  };

  const pasarAEnterprise = async (empresa) => {
    if (esEnterprise(empresa)) {
      alert(`${empresa.nombre} ya tiene membresía Enterprise.`);
      return;
    }
    if (!window.confirm(`¿Asignar membresía Enterprise a "${empresa.nombre}"?\n\nSe actualizará la suscripción (hasta 100 productos, 30 campañas).`)) {
      return;
    }
    setPasarAEnterpriseLoading(empresa.id);
    try {
      const newPlan = getPlanById("enterprise");
      const endDate = new Date();
      endDate.setFullYear(endDate.getFullYear() + 1);

      const subscriptionRef = doc(db, "subscriptions", empresa.id);
      const subscriptionData = {
        empresaId: empresa.id,
        planId: "enterprise",
        status: SUBSCRIPTION_STATUS.ACTIVE,
        startDate: new Date(),
        endDate,
        billingCycle: BILLING_CYCLES.MONTHLY,
        autoRenew: true,
        features: newPlan.features,
        limitations: newPlan.limitations,
        updatedAt: new Date()
      };
      await setDoc(subscriptionRef, subscriptionData, { merge: true });

      const empresaRef = doc(db, "empresas", empresa.id);
      await updateDoc(empresaRef, {
        plan: "enterprise",
        membresia: {
          plan: "enterprise",
          fechaInicio: new Date(),
          fechaVencimiento: endDate,
          estado: "activa",
          caracteristicas: newPlan.features,
          limitaciones: newPlan.limitations
        },
        nivel_suscripcion: "enterprise",
        fecha_actualizacion_plan: new Date(),
        fecha_actualizacion: new Date()
      });

      alert(`✅ ${empresa.nombre} asignada a membresía Enterprise exitosamente.`);
    } catch (error) {
      console.error("Error asignando Enterprise:", error);
      alert(`Error al asignar Enterprise: ${error.message}`);
    } finally {
      setPasarAEnterpriseLoading(null);
    }
  };

  const generarDatosPrueba = async (empresa) => {
    if (!window.confirm(
      `¿Generar productos y campañas de prueba para "${empresa.nombre}"?\n\n` +
      `Se crearán productos y campañas de ejemplo basados en las categorías y marcas de la empresa.`
    )) {
      return;
    }

    setGenerandoDatos(empresa.id);
    try {
      const resultado = await GenerarDatosPruebaService.generarDatosPruebaEmpresa(empresa, {
        cantidadProductos: 6, // Generar 6 productos
        cantidadCampañas: 2   // Generar 2 campañas
      });

      if (resultado.exito) {
        alert(
          `✅ Datos de prueba generados exitosamente para "${empresa.nombre}"\n\n` +
          `📦 Productos creados: ${resultado.productosCreados}\n` +
          `🎯 Campañas creadas: ${resultado.campañasCreadas}\n\n` +
          (resultado.errores.length > 0 
            ? `⚠️ Advertencias:\n${resultado.errores.slice(0, 3).join('\n')}`
            : '')
        );
      } else {
        alert(`❌ Error generando datos de prueba:\n${resultado.errores.join('\n')}`);
      }
    } catch (error) {
      console.error('Error generando datos de prueba:', error);
      alert(`❌ Error: ${error.message}`);
    } finally {
      setGenerandoDatos(null);
    }
  };

  const eliminarEmpresaDuplicada = async (empresa, motivo = '') => {
    const estadoTxt = empresa?.estado || 'sin estado';
    const msg1 =
      `Eliminar empresa duplicada:\n\n` +
      `- Nombre: ${empresa?.nombre || empresa?.id}\n` +
      `- Estado: ${estadoTxt}\n` +
      `- Motivo: ${motivo || 'coincidencia detectada'}\n\n` +
      `Esta acción borra el documento en Firestore y NO se puede deshacer.\n\n` +
      `¿Deseas continuar?`;
    if (!window.confirm(msg1)) return;

    const confirmText = window.prompt(
      `Confirmación final:\n` +
        `Escribe ELIMINAR para borrar definitivamente "${empresa?.nombre || empresa?.id}".`
    );
    if (confirmText !== 'ELIMINAR') {
      alert('Eliminación cancelada. Debes escribir ELIMINAR en mayúsculas para confirmar.');
      return;
    }

    setEliminandoEmpresaId(empresa.id);
    try {
      await deleteDoc(doc(db, 'empresas', empresa.id));
      if (empresaVistaPrevia?.id === empresa.id) setEmpresaVistaPrevia(null);
      if (contactVerifyEmpresa?.id === empresa.id) setContactVerifyEmpresa(null);
      alert('Empresa duplicada eliminada.');
    } catch (error) {
      console.error('Error eliminando empresa duplicada:', error);
      alert('Error al eliminar empresa duplicada: ' + (error.message || String(error)));
    } finally {
      setEliminandoEmpresaId(null);
    }
  };



  const empresasFiltradas = useMemo(() => {
    const base = empresas.filter((e) => empresaCumpleFiltrosListadoAdmin(e, filtros));
    const key = String(activadoPorFiltro || '').trim().toLowerCase();
    if (!key) return base;
    return base.filter((e) => String(e?.activado_por || '').trim().toLowerCase() === key);
  }, [empresas, filtros, activadoPorFiltro]);

  const empresasActivasParaQr = useMemo(() => {
    return empresas.filter((e) => empresaEsActivaParaQr(e));
  }, [empresas]);

  const empresasActivasConUrlWww = useMemo(() => {
    return empresasActivasParaQr.filter((e) => String(e?.perfilPublicoUrl || '').includes('www.kawescar.cl'));
  }, [empresasActivasParaQr]);

  const handleBulkRegenerarQrActivas = async () => {
    if (bulkQrState.running) return;

    const target = empresasActivasConUrlWww;
    const totalTarget = target.length;
    if (totalTarget === 0) {
      alert('No hay empresas activas/validadas con perfilPublicoUrl en "www" para regenerar QR.');
      return;
    }

    const msg =
      `Esto regenerará el QR para ${totalTarget} empresas activas/validadas que aún tienen perfilPublicoUrl en "www".\n\n` +
      `Se actualizará perfilPublicoUrl a https://kawescar.cl/empresa/<id> y se subirá un nuevo QR a Storage.\n\n` +
      `¿Continuar?`;
    if (!window.confirm(msg)) return;

    setBulkQrState({
      running: true,
      total: totalTarget,
      done: 0,
      ok: 0,
      fail: 0,
      current: '',
      lastError: '',
    });

    let ok = 0;
    let fail = 0;

    const BATCH_SIZE = 100;
    const total = target.length;
    for (let start = 0; start < total; start += BATCH_SIZE) {
      const end = Math.min(start + BATCH_SIZE, total);
      const batch = target.slice(start, end);

      for (let i = 0; i < batch.length; i++) {
        const globalIndex = start + i;
        const emp = batch[i];
        const id = emp?.id;
        const nombre = String(emp?.nombre || emp?.nombre_empresa || id || '').trim();
        if (!id) continue;

        setBulkQrState((s) => ({
          ...s,
          current: nombre || id,
          done: globalIndex,
        }));

        try {
          await generateOrRegenerateCompanyPublicProfile(id, { regenerateQrOnly: true });
          ok += 1;
          setBulkQrState((s) => ({ ...s, ok, lastError: '' }));
        } catch (e) {
          fail += 1;
          const msgErr = String(e?.message || e || 'Error desconocido');
          console.error('Bulk regenerar QR error:', { id, error: e });
          setBulkQrState((s) => ({ ...s, fail, lastError: `${nombre || id}: ${msgErr}` }));
        }

        setBulkQrState((s) => ({ ...s, done: globalIndex + 1 }));
      }

      // Confirmación entre lotes (permite pausar/cancelar sin perder lo avanzado)
      if (end < total) {
        const continuar = window.confirm(
          `Lote completado: ${end}/${total}.\n\nOK: ${ok}\nErrores: ${fail}\n\n¿Continuar con el siguiente lote de ${BATCH_SIZE}?`
        );
        if (!continuar) break;
      }
    }

    setBulkQrState((s) => ({ ...s, running: false, current: '' }));
    alert(`Regeneración masiva completada.\n\nOK: ${ok}\nErrores: ${fail}`);
  };

  const coincidenciasEmpresas = useMemo(() => {
    const groups = [];
    const pushGroups = (type, label, getValue) => {
      const map = new Map();
      empresas.forEach((emp) => {
        const key = getValue(emp);
        if (!key || key.length < 3) return;
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(emp);
      });
      map.forEach((list, key) => {
        const uniqueById = Array.from(new Map(list.map((x) => [x.id, x])).values());
        if (uniqueById.length >= 2) {
          groups.push({ type, label, key, empresas: uniqueById });
        }
      });
    };

    pushGroups('nombre', 'Nombre similar', (e) => normalizeDuplicateValue(e.nombre));
    pushGroups('email', 'Email igual', (e) => normalizeDuplicateValue(e.email || e.email_empresa));
    pushGroups('telefono', 'Teléfono/WhatsApp igual', (e) =>
      normalizePhoneForDuplicate(e.telefono || e.whatsapp || e.telefono_fijo)
    );
    pushGroups('web', 'Sitio web igual', (e) => normalizeWebForDuplicate(getPaginaWeb(e) || e.web));
    pushGroups('direccion', 'Dirección igual', (e) => normalizeDuplicateValue(e.direccion || e.direccion_empresa));

    // Consolidar grupos repetidos por mismo set de IDs para no inundar la vista.
    const merged = new Map();
    groups.forEach((g) => {
      const ids = g.empresas.map((x) => x.id).sort().join('|');
      const sig = `${ids}`;
      if (!merged.has(sig)) {
        merged.set(sig, { ...g, labels: [g.label] });
      } else {
        const prev = merged.get(sig);
        prev.labels.push(g.label);
      }
    });

    return Array.from(merged.values())
      .map((g) => ({ ...g, label: g.labels.join(' + ') }))
      .sort((a, b) => b.empresas.length - a.empresas.length)
      .slice(0, 50);
  }, [empresas]);

  /** Ingresada vs empresas activas: nombre, teléfono, web, dirección (misma normalización que duplicados). */
  const coincidenciaIngresadaVsActivas = useMemo(() => {
    const activas = empresas.filter((e) => e.estado === ESTADOS_EMPRESA.ACTIVA);
    const map = new Map();

    const camposQueCoinciden = (a, b) => {
      const campos = [];
      const n1 = normalizeDuplicateValue(a.nombre);
      const n2 = normalizeDuplicateValue(b.nombre);
      if (n1 && n2 && n1 === n2) campos.push('nombre');

      const t1 = normalizePhoneForDuplicate(a.telefono || a.whatsapp || a.telefono_fijo);
      const t2 = normalizePhoneForDuplicate(b.telefono || b.whatsapp || b.telefono_fijo);
      if (t1 && t2 && t1 === t2) campos.push('teléfono');

      const w1 = normalizeWebForDuplicate(getPaginaWeb(a) || a.web);
      const w2 = normalizeWebForDuplicate(getPaginaWeb(b) || b.web);
      if (w1 && w2 && w1 === w2) campos.push('web');

      const d1 = normalizeDuplicateValue(a.direccion || a.direccion_empresa);
      const d2 = normalizeDuplicateValue(b.direccion || b.direccion_empresa);
      if (d1 && d2 && d1 === d2) campos.push('dirección');

      return campos;
    };

    empresas.forEach((ing) => {
      if (ing.estado !== ESTADOS_EMPRESA.INGRESADA) return;
      const matches = [];
      activas.forEach((act) => {
        if (act.id === ing.id) return;
        const campos = camposQueCoinciden(ing, act);
        if (campos.length) {
          matches.push({
            activaId: act.id,
            activaNombre: act.nombre || act.id,
            campos
          });
        }
      });
      map.set(ing.id, matches);
    });
    return map;
  }, [empresas]);

  const eliminarEmpresa = async (empresa) => {
    // Si hay selección previa, el botón eliminar de cualquier fila dispara eliminación masiva (solo "ingresada").
    if (empresasSeleccionadasIngresadasIds.length > 0) {
      const handled = await eliminarEmpresasSeleccionadasIngresadas();
      if (handled) return;
    }

    if (!puedeEliminarDesdeListado(empresa.estado)) {
      alert(
        'Solo se pueden eliminar empresas en estado Inactiva o Ingresada. Si corresponde, desactívala desde Editar y vuelve a intentar.'
      );
      return;
    }
    const esInactiva = estadoEsInactiva(empresa.estado);
    const esIng = estadoEsIngresada(empresa.estado);
    const coincidencias = esIng ? coincidenciaIngresadaVsActivas.get(empresa.id) || [] : [];
    const advCoinc =
      esIng && coincidencias.length > 0
        ? '\n\nAdvertencia: hay coincidencia con al menos una empresa activa (nombre, teléfono, web o dirección). Revisa antes de borrar.'
        : '';

    if (
      !window.confirm(
        `¿Eliminar definitivamente "${empresa.nombre}"?\n\n` +
          (esInactiva
            ? 'Estado: inactiva. Se borrará el documento en Firestore. No se puede deshacer.'
            : 'Estado: ingresada. Se borrará el documento en Firestore. No se puede deshacer.') +
          advCoinc
      )
    ) {
      return;
    }
    setEliminandoEmpresaId(empresa.id);
    try {
      await deleteDoc(doc(db, 'empresas', empresa.id));
      if (empresaVistaPrevia?.id === empresa.id) setEmpresaVistaPrevia(null);
      if (contactVerifyEmpresa?.id === empresa.id) setContactVerifyEmpresa(null);
      alert('Empresa eliminada.');
    } catch (error) {
      console.error('Error eliminando empresa:', error);
      alert('Error al eliminar la empresa: ' + (error.message || String(error)));
    } finally {
      setEliminandoEmpresaId(null);
    }
  };

  const regionesUnicas = [...new Set(empresas.map(emp => (emp.region || emp.region_empresa || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, "es"));
  const comunasUnicas = [...new Set(empresas.map(emp => (emp.comuna || emp.comuna_empresa || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, "es"));
  const rubrosUnicos = [...new Set(empresas.map((emp) => String(emp.rubro || emp.categoria || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'es'));
  const tiposEmpresaUnicos = [...new Set(empresas.map((emp) => String(emp.tipoEmpresa || emp.tipo_empresa || emp.tipo || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'es'));
  const serviciosUnicos = useMemo(() => {
    const out = new Set();
    empresas.forEach((emp) => {
      const servicios = Array.isArray(emp?.servicios) ? emp.servicios : [];
      servicios
        .map((s) => (typeof s === 'string' ? s : s?.nombre || s?.id || ''))
        .filter(Boolean)
        .forEach((s) => out.add(String(s).trim()));
    });
    return [...out].sort((a, b) => a.localeCompare(b, 'es'));
  }, [empresas]);

  const marcasUnicas = useMemo(() => {
    const out = new Set();
    empresas.forEach((emp) => {
      getMarcasPreview(emp).forEach((m) => out.add(String(m).trim()));
    });
    return [...out].sort((a, b) => a.localeCompare(b, 'es'));
  }, [empresas]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando empresas...</p>
          <p className="text-gray-400 text-sm mt-2">Conectando con Firebase...</p>
        </div>
      </div>
    );
  }

  // Mostrar mensaje si no hay empresas
  if (!loading && empresas.length === 0) {
    return (
      <section className="p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Gestión de Empresas Registradas</h2>
          <p className="text-gray-600">
            Administra empresas y proveedores que ya están registrados en el sistema
          </p>
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
          <span className="text-6xl mb-4 block">🏢</span>
          <h3 className="text-lg font-medium text-yellow-900 mb-2">No hay empresas registradas</h3>
          <p className="text-yellow-700 mb-4">
            Parece que aún no se han registrado empresas en el sistema, o hay un problema de conexión con Firebase.
          </p>
          
          <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-4 mb-6 text-left">
            <h4 className="font-medium text-yellow-900 mb-2">💡 Posibles causas:</h4>
            <ul className="text-yellow-800 text-sm space-y-1">
              <li>• La colección "empresas" está vacía en Firebase</li>
              <li>• Problemas de permisos de lectura en Firebase</li>
              <li>• Problemas de conexión a internet</li>
              <li>• Las empresas están en una colección diferente</li>
            </ul>
          </div>
          
          <div className="space-x-4">
            <button
              onClick={() => navigate("/admin/solicitudes-registro")}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              📋 Ver Solicitudes
            </button>
            <button
              onClick={() => navigate("/admin/proveedores")}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
            >
              🏪 Vista Unificada
            </button>
            <button
              onClick={crearDatosEjemplo}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              🏗️ Crear Empresas
            </button>
            <button
              onClick={crearSolicitudesDeEjemplo}
              className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
            >
              📋 Crear Solicitudes
            </button>
            <button
              onClick={() => window.location.reload()}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              🔄 Recargar Página
            </button>
          </div>
          
          <div className="mt-6 text-xs text-yellow-600">
            💡 Tip: Revisa la consola del navegador (F12) para más detalles del error
          </div>
        </div>
      </section>
    );
  }

  if (isDuplicateView) {
    return (
      <section className="p-6">
        <div className="mb-6 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Empresas posiblemente repetidas</h2>
            <p className="text-gray-600">
              Coincidencias detectadas por nombre, email, teléfono, web o dirección (sin importar estado).
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/admin/empresas')}
            className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
          >
            ← Volver al listado
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-rose-200 p-6">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="text-sm text-gray-600">Revisión de duplicados detectados automáticamente.</div>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
              {coincidenciasEmpresas.length} grupo{coincidenciasEmpresas.length !== 1 ? 's' : ''}
            </span>
          </div>
          {coincidenciasEmpresas.length === 0 ? (
            <p className="text-sm text-gray-500">No se detectaron coincidencias relevantes.</p>
          ) : (
            <div className="space-y-4 max-h-[70vh] overflow-auto pr-1">
              {coincidenciasEmpresas.map((grupo, idx) => (
                <div key={`${grupo.type}-${idx}`} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-semibold text-gray-800">{grupo.label}</div>
                    <div className="text-xs text-gray-500">{grupo.empresas.length} empresas</div>
                  </div>
                  <div className="space-y-2">
                    {grupo.empresas.map((emp) => (
                      <div key={emp.id} className="bg-white border border-gray-200 rounded p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 truncate">{emp.nombre || 'Sin nombre'}</div>
                          <div className="text-xs text-gray-500 truncate">
                            {emp.email || emp.email_empresa || 'sin email'} · {emp.telefono || emp.whatsapp || emp.telefono_fijo || 'sin teléfono'}
                          </div>
                          <div className="mt-1">
                            <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded ${colorEstado(emp.estado)}`}>
                              {emp.estado || 'sin estado'}
                            </span>
                            <span className="ml-2 text-[10px] text-gray-400">{emp.id}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => window.open(`/admin/editar-empresa/${emp.id}`, '_blank', 'noopener,noreferrer')}
                            className="px-2.5 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => eliminarEmpresaDuplicada(emp, grupo.label)}
                            disabled={eliminandoEmpresaId === emp.id}
                            className="px-2.5 py-1.5 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-60"
                            title="Eliminar empresa duplicada"
                          >
                            {eliminandoEmpresaId === emp.id ? 'Eliminando…' : 'Eliminar'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="p-6">
      {mostrarImportarJson && (
        <ImportarEmpresasPublicasJson
          onClose={() => setMostrarImportarJson(false)}
          onSuccess={() => setMostrarImportarJson(false)}
        />
      )}
      <div className="mb-6 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Listado de empresas</h1>
          <p className="text-gray-600">
            Filtra y gestiona proveedores. Métricas agregadas y mapa por comunas están en el menú <strong>Directorio</strong>.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 justify-end">
          <button
            type="button"
            onClick={() => navigate('/admin/directorio/metricas')}
            className="bg-slate-800 text-white px-4 py-2 rounded-lg hover:bg-slate-900 transition-colors font-medium text-sm"
          >
            📊 Métricas y números
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/directorio/zonas')}
            className="bg-slate-800 text-white px-4 py-2 rounded-lg hover:bg-slate-900 transition-colors font-medium text-sm"
          >
            🗺️ Zonas y comunas
          </button>
          <button
            onClick={() => navigate('/admin/empresas?vista=repetidas')}
            className="bg-rose-600 text-white px-4 py-2 rounded-lg hover:bg-rose-700 transition-colors font-medium text-sm"
          >
            🧬 Empresas repetidas
          </button>
          <button
            onClick={() => setMostrarImportarJson(true)}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors font-medium text-sm"
          >
            📥 Importar (JSON)
          </button>
          <button
            onClick={() => navigate("/admin/crear-empresa-publica")}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
          >
            ➕ Crear empresa
          </button>
        </div>
      </div>

      {cargandoSesionesYVisitas && (
        <p className="mb-4 text-xs text-amber-800 inline-flex items-center gap-2">
          <span className="inline-block w-3.5 h-3.5 border-2 border-amber-400 border-t-amber-800 rounded-full animate-spin shrink-0" aria-hidden />
          Cargando visitas y sesiones para el listado…
        </p>
      )}

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-11 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
            <select
              value={filtros.estado}
              onChange={(e) => setFiltros({...filtros, estado: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {ESTADOS_FILTRO_ADMIN.map(est => (
                <option key={est.value} value={est.value}>{est.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Activado por</label>
            <select
              value={activadoPorFiltro}
              onChange={(e) => setActivadoPorFiltro(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              {activadoresDisponibles
                .sort((a, b) => getAdminDisplayNameFromEmail(a).localeCompare(getAdminDisplayNameFromEmail(b), 'es'))
                .map((emailLower) => {
                  const email = String(emailLower || '').trim();
                  if (!email) return null;
                  const label = getAdminDisplayNameFromEmail(email);
                  return (
                    <option key={email} value={email}>
                      {label}
                    </option>
                  );
                })}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Latitud</label>
            <select
              value={filtros.latitud}
              onChange={(e) => setFiltros({ ...filtros, latitud: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todas</option>
              <option value="vacia">Vacía (sin latitud válida)</option>
              <option value="con_datos">Con datos</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Región</label>
            <select
              value={filtros.region}
              onChange={(e) => setFiltros({...filtros, region: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todas</option>
              <option value="__sin_region__">Sin región</option>
              {regionesUnicas.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Comuna</label>
            <select
              value={filtros.comuna}
              onChange={(e) => setFiltros({...filtros, comuna: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todas</option>
              <option value="__sin_comuna__">Sin comuna</option>
              {comunasUnicas.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tiene Web</label>
            <select
              value={filtros.tieneWeb}
              onChange={(e) => setFiltros({...filtros, tieneWeb: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value="si">Con Web</option>
              <option value="no">Sin Web</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">QR perfil público</label>
            <select
              value={filtros.qrGenerado}
              onChange={(e) => setFiltros({ ...filtros, qrGenerado: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value="si">Con QR generado</option>
              <option value="no">Sin QR generado</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Servicios</label>
            <select
              value={filtros.servicio}
              onChange={(e) => setFiltros({ ...filtros, servicio: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value="__sin_servicio__">Sin servicio</option>
              {serviciosUnicos.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Marca</label>
            <select
              value={filtros.marca}
              onChange={(e) => setFiltros({ ...filtros, marca: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todas</option>
              <option value="__sin_marca__">Sin marca</option>
              {marcasUnicas.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Rubro</label>
            <select
              value={filtros.rubro}
              onChange={(e) => setFiltros({ ...filtros, rubro: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value="__sin_rubro__">Sin rubro / categoría</option>
              {rubrosUnicos.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tipo empresa</label>
            <select
              value={filtros.tipoEmpresa}
              onChange={(e) => setFiltros({ ...filtros, tipoEmpresa: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              <option value="__sin_tipo__">Sin tipo</option>
              {tiposEmpresaUnicos.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setFiltros({ ...FILTROS_EMPRESAS_ADMIN_INITIAL });
                setActivadoPorFiltro('');
              }}
              className="w-full px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Limpiar
            </button>
          </div>
        </div>
      </div>

      {/* Buscador principal (debajo de los filtros para acceso rápido) */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Buscar</label>
        <input
          type="text"
          placeholder="Nombre, email, dirección..."
          value={filtros.busqueda}
          onChange={(e) => setFiltros({...filtros, busqueda: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Resultados (orden natural del snapshot) */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium text-gray-900">
              Empresas ({empresasFiltradas.length})
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              {empresasSeleccionadasIngresadasIds.length > 0 ? (
                <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-teal-50 border border-teal-200">
                  <span className="text-sm font-semibold text-teal-800">
                    Seleccionadas: {empresasSeleccionadasIngresadasIds.length}
                  </span>
                  <button
                    type="button"
                    onClick={limpiarSeleccionEmpresas}
                    className="text-xs font-semibold text-teal-800 hover:text-teal-900 underline"
                    title="Limpiar selección"
                  >
                    Limpiar
                  </button>
                </div>
              ) : null}
              <button
                type="button"
                onClick={handleBulkRegenerarQrActivas}
                disabled={bulkQrState.running || empresasActivasConUrlWww.length === 0}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  bulkQrState.running || empresasActivasConUrlWww.length === 0
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
                title="Regenera el QR para empresas activas/validadas cuyo perfilPublicoUrl aún apunta a www"
              >
                {bulkQrState.running
                  ? `♻️ Regenerando QR... (${bulkQrState.done}/${bulkQrState.total})`
                  : `♻️ Regenerar QR con www (${empresasActivasConUrlWww.length})`}
              </button>
              <button
                type="button"
                onClick={() => exportEmpresasActivasExcel(empresas)}
                className="px-3 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors"
                title="Descargar Excel con empresas en estado activa"
              >
                ⬇️ Excel activas
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded ${viewMode === 'table' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}
                aria-label="Vista tabla"
              >
                📋 Tabla
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}
                aria-label="Vista tarjetas"
              >
                📱 Tarjetas
              </button>
            </div>
          </div>

          {bulkQrState.running && (
            <div className="mt-3 bg-indigo-50 border border-indigo-200 rounded-lg p-3">
              <p className="text-sm text-indigo-900">
                <strong>Regeneración masiva en progreso:</strong>{' '}
                {bulkQrState.current ? `Procesando: ${bulkQrState.current}` : 'Iniciando...'}
              </p>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div className="bg-white rounded border border-indigo-100 px-2 py-1">
                  <span className="text-indigo-700 font-semibold">Total:</span> {bulkQrState.total}
                </div>
                <div className="bg-white rounded border border-indigo-100 px-2 py-1">
                  <span className="text-indigo-700 font-semibold">Hechas:</span> {bulkQrState.done}
                </div>
                <div className="bg-white rounded border border-indigo-100 px-2 py-1">
                  <span className="text-emerald-700 font-semibold">OK:</span> {bulkQrState.ok}
                </div>
                <div className="bg-white rounded border border-indigo-100 px-2 py-1">
                  <span className="text-rose-700 font-semibold">Errores:</span> {bulkQrState.fail}
                </div>
              </div>
              {bulkQrState.lastError ? (
                <p className="mt-2 text-xs text-rose-700 break-words">
                  <strong>Último error:</strong> {bulkQrState.lastError}
                </p>
              ) : null}
            </div>
          )}
          
          {/* Información sobre el botón de perfil del proveedor */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <span className="text-green-600">💡</span>
              <p className="text-sm text-green-800">
                <strong>Nombre rápido:</strong> haz clic en el nombre de la empresa en la tabla o en tarjetas para editarlo sin abrir el formulario completo.
                <strong className="ml-1">Marcas:</strong> si no hay marca, usa el botón <strong>Multimarca</strong> en la columna Marcas.
                <span className="block mt-1">
                <strong>Acceso rápido al perfil del proveedor:</strong> Usa el botón verde &quot;Ver dashboard&quot; / perfil público
                para ver lo que ven los clientes. <strong>Eliminar</strong> está disponible para empresas en estado <strong>Inactiva</strong> o <strong>Ingresada</strong> (borrado definitivo del documento en Firestore).
                </span>
              </p>
            </div>
          </div>
        </div>

        {viewMode === 'table' ? (
          <div className="overflow-x-auto">
            <table className="min-w-full table-fixed" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '18%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '9%' }} />
                <col style={{ width: '9%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '4%' }} />
                <col style={{ width: '16%' }} />
              </colgroup>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Empresa</th>
                  <th className="px-2 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Web</th>
                  <th className="px-2 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Email</th>
                  <th className="px-2 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Servicios</th>
                  <th className="px-2 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Marcas</th>
                  <th className="px-2 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Estado</th>
                  <th className="px-2 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                    <span className="sr-only">Seleccionar</span>
                  </th>
                  <th className="px-2 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Coincidencia</th>
                  <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wide">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {empresasFiltradas.map((empresa) => {
                  const servicios = getServiciosPreview(empresa);
                  const marcas = getMarcasPreview(empresa);
                  const serviciosHead = servicios.slice(0, 2);
                  const marcasHead = marcas.slice(0, 2);
                  const serviciosRest = servicios.length - serviciosHead.length;
                  const marcasRest = marcas.length - marcasHead.length;
                  const web = getPaginaWeb(empresa) || empresa.web || '';
                  const webShort = web ? web.replace(/^https?:\/\//, '').replace(/\/$/, '') : '';
                  const mailEmpresa = (empresa.email || empresa.email_empresa || '').trim();
                  const planEmpresa = empresa.plan || empresa.membresia?.plan || '';
                  const esPremiumEmpresa = planEmpresa === 'premium';
                  const esCorporateEmpresa = planEmpresa === 'enterprise';
                  const contactoVerificado = buildPublicVerificationState(empresa);
                  const coincidenciasIngresada =
                    empresa.estado === ESTADOS_EMPRESA.INGRESADA
                      ? coincidenciaIngresadaVsActivas.get(empresa.id) || []
                      : null;

                  return (
                    <tr key={empresa.id} className="hover:bg-gray-50 align-middle">
                      <td className="px-3 py-1.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="relative flex-shrink-0">
                            <LogoImage
                              logo={empresa.logo_url || empresa.logo}
                              nombre={empresa.nombre}
                              className="w-9 h-9 object-contain rounded border bg-white flex-shrink-0"
                            />
                            {contactoVerificado ? (
                              <span
                                className="absolute -top-1 -right-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-500 text-white text-[10px] font-bold border border-white"
                                title="Contacto verificado"
                                aria-label="Contacto verificado"
                              >
                                ✓
                              </span>
                            ) : null}
                          </div>
                          <button
                            type="button"
                            onClick={() => setContactVerifyEmpresa({ id: empresa.id, ...empresa })}
                            className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-lg border ${
                              contactoVerificado
                                ? 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100'
                                : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                            }`}
                            title={contactoVerificado ? 'Contacto verificado' : 'Contacto pendiente de verificación'}
                            aria-label={contactoVerificado ? 'Contacto verificado' : 'Contacto pendiente de verificación'}
                          >
                            <span className="text-sm font-bold leading-none">{contactoVerificado ? '✓' : '!'}</span>
                          </button>
                          <span
                            className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded text-xs"
                            title={esPremiumEmpresa ? 'Premium' : esCorporateEmpresa ? 'Corporate' : 'Básica'}
                            aria-label={esPremiumEmpresa ? 'Premium' : esCorporateEmpresa ? 'Corporate' : 'Básica'}
                          >
                            {esPremiumEmpresa ? (
                              <span className="text-amber-500" title="Premium">👑</span>
                            ) : esCorporateEmpresa ? (
                              <span className="text-slate-600" title="Corporate">🏢</span>
                            ) : (
                              <span className="text-gray-400 font-medium text-[10px]" title="Básica">B</span>
                            )}
                          </span>
                          <div className="min-w-0 flex-1">
                            {nombreRapidoEditId === empresa.id ? (
                              <div className="space-y-1" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="text"
                                  value={nombreRapidoValor}
                                  onChange={(e) => setNombreRapidoValor(e.target.value)}
                                  className="w-full text-sm font-semibold text-gray-900 border border-blue-400 rounded px-1.5 py-0.5 focus:ring-1 focus:ring-blue-500"
                                  autoFocus
                                  disabled={nombreRapidoGuardando}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      guardarNombreRapido();
                                    }
                                    if (e.key === 'Escape') {
                                      e.preventDefault();
                                      cancelarEdicionNombreRapido();
                                    }
                                  }}
                                />
                                <div className="flex flex-wrap gap-1">
                                  <button
                                    type="button"
                                    onClick={() => guardarNombreRapido()}
                                    disabled={nombreRapidoGuardando}
                                    className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                                  >
                                    {nombreRapidoGuardando ? '…' : 'Guardar'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => cancelarEdicionNombreRapido()}
                                    disabled={nombreRapidoGuardando}
                                    className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
                                  >
                                    Cancelar
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => iniciarEdicionNombreRapido(empresa)}
                                className="font-semibold text-gray-900 text-sm truncate leading-tight text-left w-full hover:text-blue-700 hover:underline"
                                title="Clic para editar el nombre rápido"
                              >
                                {empresa.nombre || 'Sin nombre'}
                              </button>
                            )}
                            <div className="text-[11px] text-gray-500 truncate leading-tight mt-0.5" title={empresa.direccion || ''}>
                              {empresa.direccion || '—'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-1.5 align-middle">
                        {webShort ? (
                          <a
                            href={web.startsWith('http') ? web : `https://${web}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[11px] text-blue-600 hover:text-blue-800 truncate block max-w-full"
                            title={webShort}
                          >
                            {webShort.length > 18 ? webShort.slice(0, 18) + '…' : webShort}
                          </a>
                        ) : (
                          <span className="text-[11px] text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 align-middle">
                        {mailEmpresa ? (
                          <a
                            href={`mailto:${mailEmpresa}`}
                            className="text-[11px] text-blue-600 hover:text-blue-800 truncate block max-w-full"
                            title={mailEmpresa}
                          >
                            {mailEmpresa.length > 28 ? `${mailEmpresa.slice(0, 28)}…` : mailEmpresa}
                          </a>
                        ) : (
                          <span className="text-[11px] text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 align-middle">
                        <div className="flex flex-wrap gap-0.5">
                          {serviciosHead.map((s) => (
                            <span key={s} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-sky-50 text-sky-700 max-w-[80px] truncate" title={s}>
                              {s.length > 10 ? s.slice(0, 10) + '…' : s}
                            </span>
                          ))}
                          {serviciosRest > 0 && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-sky-50 text-sky-600">
                              +{serviciosRest}
                            </span>
                          )}
                          {!servicios.length && <span className="text-[10px] text-gray-400">—</span>}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 align-middle">
                        <div className="flex flex-wrap gap-0.5">
                          {marcasHead.map((m) => (
                            <span key={m} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 max-w-[80px] truncate" title={m}>
                              {m.length > 10 ? m.slice(0, 10) + '…' : m}
                            </span>
                          ))}
                          {marcasRest > 0 && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-600">
                              +{marcasRest}
                            </span>
                          )}
                          {!marcas.length && (
                            <button
                              type="button"
                              onClick={() => asignarMarcaMultimarca(empresa.id)}
                              disabled={marcaMultimarcaGuardandoId === empresa.id}
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-900 border border-amber-200 hover:bg-amber-200 disabled:opacity-60"
                              title="Asignar marca «Multimarca» al documento"
                            >
                              {marcaMultimarcaGuardandoId === empresa.id ? '…' : 'Multimarca'}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 align-middle">
                        <div className="flex flex-wrap items-center gap-1">
                          <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded ${colorEstado(empresa.estado)}`}>
                            {empresa.estado || '—'}
                          </span>
                          {empresa?.activado_por ? (
                            <span
                              className="inline-flex items-center justify-center w-5 h-5 rounded-md border border-gray-200 bg-white text-gray-700"
                              title={`Activado por: ${getAdminDisplayNameFromEmail(empresa.activado_por)}`}
                              aria-label={`Activado por: ${getAdminDisplayNameFromEmail(empresa.activado_por)}`}
                            >
                              👤
                            </span>
                          ) : null}
                          <span
                            className={`inline-flex w-2 h-2 rounded-full flex-shrink-0 ${isPerfilOnline(empresa.id) ? 'bg-green-500' : 'bg-gray-300'}`}
                            title={isPerfilOnline(empresa.id) ? 'En línea' : formatLastSession(empresa.id)}
                          />
                          <span className="text-[10px] text-gray-500" title="Visitas perfil">
                            {visitasPerfilPorEmpresa[empresa.id] ?? 0}
                          </span>
                        </div>
                      </td>
                      <td className="px-2 py-1.5 align-middle">
                        {coincidenciasIngresada === null ? (
                          <span className="text-[10px] text-gray-400">—</span>
                        ) : coincidenciasIngresada.length === 0 ? (
                          <span
                            className="text-[10px] text-gray-500"
                            title="Sin coincidencia con empresas activas (nombre, teléfono, web, dirección)"
                          >
                            No
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-900"
                            title={coincidenciasIngresada
                              .map((m) => `${m.activaNombre}: ${m.campos.join(', ')}`)
                              .join(' · ')}
                          >
                            Sí
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 align-middle">
                        {estadoEsIngresada(empresa.estado) ? (
                          <input
                            type="checkbox"
                            checked={empresasSeleccionadasIngresadasIds.includes(empresa.id)}
                            onChange={(e) => toggleEmpresaSeleccionada(empresa, e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                            aria-label={`Seleccionar ${empresa.nombre}`}
                          />
                        ) : (
                          <span className="inline-block h-4 w-4" aria-hidden />
                        )}
                      </td>
                      <td className="px-3 py-1.5 align-middle">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <button
                            onClick={() => window.open(`/admin/editar-empresa/${empresa.id}`, '_blank', 'noopener,noreferrer')}
                            className="inline-flex items-center px-2 py-1 rounded text-[11px] font-medium bg-blue-600 text-white hover:bg-blue-700"
                            title="Editar empresa"
                          >
                            Editar
                          </button>
                          {empresa.estado === ESTADOS_EMPRESA.ACTIVA ? (
                            <button
                              onClick={() => window.open(`/perfil-proveedor/${empresa.id}`, '_blank')}
                              className="inline-flex items-center px-2 py-1 rounded text-[11px] font-medium bg-emerald-600 text-white hover:bg-emerald-700"
                              title="Ver dashboard"
                            >
                              Ver dashboard
                            </button>
                          ) : (
                            <span
                              className="inline-flex items-center px-2 py-1 rounded text-[11px] font-medium bg-emerald-100 text-emerald-400 cursor-not-allowed opacity-70"
                              title="Solo disponible para empresas activas"
                            >
                              Ver dashboard
                            </span>
                          )}
                          {empresa.estado === ESTADOS_EMPRESA.ACTIVA ? (
                            <button
                              onClick={() => window.open(`/empresa/${empresa.id}`, '_blank')}
                              className="inline-flex items-center px-2 py-1 rounded text-[11px] font-medium bg-slate-100 text-slate-700 hover:bg-slate-200"
                              title="Ver perfil público"
                            >
                              Perfil
                            </button>
                          ) : (
                            <span
                              className="inline-flex items-center px-2 py-1 rounded text-[11px] font-medium bg-slate-100 text-slate-300 cursor-not-allowed opacity-70"
                              title="Solo disponible para empresas activas"
                            >
                              Perfil
                            </span>
                          )}
                          {puedeEliminarDesdeListado(empresa.estado) && (
                            <button
                              type="button"
                              onClick={() => eliminarEmpresa(empresa)}
                              disabled={eliminandoEmpresaId === '__bulk__' || eliminandoEmpresaId === empresa.id}
                              className="inline-flex items-center px-2 py-1 rounded text-[11px] font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
                              title="Eliminar empresa (inactiva o ingresada)"
                            >
                              {eliminandoEmpresaId === empresa.id ? '…' : 'Eliminar'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          // Vista de tarjetas
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {empresasFiltradas.map((empresa) => {
              const planEmpresaCard = empresa.plan || empresa.membresia?.plan || '';
              const esPremiumCard = planEmpresaCard === 'premium';
              const esCorporateCard = planEmpresaCard === 'enterprise';
              const marcasCard = getMarcasPreview(empresa);
              return (
              <div key={empresa.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4 mb-3">
                  <LogoImage 
                    logo={empresa.logo_url || empresa.logo} 
                    nombre={empresa.nombre} 
                    className="w-16 h-16 object-contain rounded border"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      {nombreRapidoEditId === empresa.id ? (
                        <div className="flex-1 min-w-0 space-y-1">
                          <input
                            type="text"
                            value={nombreRapidoValor}
                            onChange={(e) => setNombreRapidoValor(e.target.value)}
                            className="w-full text-sm font-medium border border-blue-400 rounded px-2 py-1"
                            autoFocus
                            disabled={nombreRapidoGuardando}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                guardarNombreRapido();
                              }
                              if (e.key === 'Escape') {
                                e.preventDefault();
                                cancelarEdicionNombreRapido();
                              }
                            }}
                          />
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => guardarNombreRapido()}
                              disabled={nombreRapidoGuardando}
                              className="px-2 py-0.5 text-xs font-semibold bg-blue-600 text-white rounded"
                            >
                              {nombreRapidoGuardando ? '…' : 'Guardar'}
                            </button>
                            <button
                              type="button"
                              onClick={() => cancelarEdicionNombreRapido()}
                              className="px-2 py-0.5 text-xs bg-gray-100 rounded"
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => iniciarEdicionNombreRapido(empresa)}
                          className="font-medium text-gray-900 text-left hover:text-blue-700 hover:underline truncate"
                          title="Clic para editar el nombre rápido"
                        >
                          {empresa.nombre}
                        </button>
                      )}
                      <span title={esPremiumCard ? 'Premium' : esCorporateCard ? 'Corporate' : 'Básica'} aria-label={esPremiumCard ? 'Premium' : esCorporateCard ? 'Corporate' : 'Básica'}>
                        {esPremiumCard ? <span className="text-amber-500">👑</span> : esCorporateCard ? <span className="text-slate-600">🏢</span> : <span className="text-gray-400 text-xs font-medium">B</span>}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">{empresa.categoria}</p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                      <span className="text-xs text-gray-500">Marcas:</span>
                      {marcasCard.length > 0 ? (
                        <span className="text-xs text-amber-800">{marcasCard.slice(0, 3).join(', ')}{marcasCard.length > 3 ? '…' : ''}</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => asignarMarcaMultimarca(empresa.id)}
                          disabled={marcaMultimarcaGuardandoId === empresa.id}
                          className="px-2 py-0.5 text-xs font-semibold rounded bg-amber-100 text-amber-900 border border-amber-200 hover:bg-amber-200 disabled:opacity-60"
                        >
                          {marcaMultimarcaGuardandoId === empresa.id ? '…' : 'Multimarca'}
                        </button>
                      )}
                    </div>
                    <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full mt-1 ${colorEstado(empresa.estado)}`}>
                      {empresa.estado || "Sin estado"}
                    </span>
                    {empresa.estado === ESTADOS_EMPRESA.INGRESADA ? (
                      <div className="mt-1">
                        {(coincidenciaIngresadaVsActivas.get(empresa.id) || []).length > 0 ? (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-900"
                            title={(coincidenciaIngresadaVsActivas.get(empresa.id) || [])
                              .map((m) => `${m.activaNombre}: ${m.campos.join(', ')}`)
                              .join(' · ')}
                          >
                            Coincidencia con activa
                          </span>
                        ) : (
                          <span className="text-[10px] text-gray-500">Sin coincidencia con activas</span>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
                
                <div className="space-y-1 text-sm text-gray-600 mb-4">
                  <p>📧 {empresa.email || empresa.email_empresa || '—'}</p>
                  <p>📞 {empresa.telefono}</p>
                  <p>📍 {empresa.direccion}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${isPerfilOnline(empresa.id) ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {isPerfilOnline(empresa.id) ? '● En línea' : '○ Desconectado'}
                    </span>
                    <span className="text-xs text-gray-500">Últ. sesión: {formatLastSession(empresa.id)}</span>
                    <span className="font-medium text-gray-900" title="Clientes que visitaron el perfil">👁 Visitas perfil: {visitasPerfilPorEmpresa[empresa.id] ?? 0}</span>
                    <button
                      onClick={() => navigate(`/admin/contactos-empresa?empresaId=${empresa.id}`)}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                        (visitasPorEmpresa[empresa.id] || 0) > 0 ? 'bg-teal-100 text-teal-800 hover:bg-teal-200' : 'bg-gray-100 text-gray-500'
                      }`}
                      title="Contactos (formulario)"
                    >
                      👥 Contactos: {visitasPorEmpresa[empresa.id] || 0}
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => window.open(`/admin/editar-empresa/${empresa.id}`, '_blank', 'noopener,noreferrer')}
                    className="px-2 py-1 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700"
                    title="Editar empresa"
                  >
                    Editar
                  </button>
                  {empresa.estado === ESTADOS_EMPRESA.ACTIVA ? (
                    <button
                      onClick={() => window.open(`/perfil-proveedor/${empresa.id}`, '_blank')}
                      className="px-2 py-1 bg-emerald-600 text-white text-xs font-medium rounded hover:bg-emerald-700"
                      title="Ver dashboard"
                    >
                      Ver dashboard
                    </button>
                  ) : (
                    <span
                      className="px-2 py-1 bg-emerald-100 text-emerald-400 text-xs font-medium rounded cursor-not-allowed opacity-70"
                      title="Solo disponible para empresas activas"
                    >
                      Ver dashboard
                    </span>
                  )}
                  {empresa.estado === ESTADOS_EMPRESA.ACTIVA ? (
                    <button
                      onClick={() => window.open(`/empresa/${empresa.id}`, '_blank')}
                      className="px-2 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded hover:bg-slate-200 border border-slate-200"
                      title="Ver perfil público"
                    >
                      Perfil
                    </button>
                  ) : (
                    <span
                      className="px-2 py-1 bg-slate-100 text-slate-300 text-xs font-medium rounded border border-slate-200 cursor-not-allowed opacity-70"
                      title="Solo disponible para empresas activas"
                    >
                      Perfil
                    </span>
                  )}
                  {puedeEliminarDesdeListado(empresa.estado) && (
                    <button
                      type="button"
                      onClick={() => eliminarEmpresa(empresa)}
                      disabled={eliminandoEmpresaId === '__bulk__' || eliminandoEmpresaId === empresa.id}
                      className="px-2 py-1 bg-red-600 text-white text-xs font-medium rounded hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
                      title="Eliminar empresa (inactiva o ingresada)"
                    >
                      {eliminandoEmpresaId === empresa.id ? 'Eliminando…' : 'Eliminar'}
                    </button>
                  )}
                </div>
              </div>
            );
            })}
          </div>
        )}

        {empresasFiltradas.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">No hay empresas que coincidan con los filtros</p>
          </div>
        )}
      </div>

      {/* Modal de Vista Previa */}
      {empresaVistaPrevia && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  Vista Previa: {empresaVistaPrevia.nombre}
                </h2>
                <button
                  onClick={() => setEmpresaVistaPrevia(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Información Básica */}
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <LogoImage 
                      logo={empresaVistaPrevia.logo_url || empresaVistaPrevia.logo} 
                      nombre={empresaVistaPrevia.nombre} 
                      className="w-24 h-24 object-contain rounded border"
                    />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{empresaVistaPrevia.nombre}</h3>
                      <span className={`inline-block px-3 py-1 text-sm font-semibold rounded-full ${colorEstado(empresaVistaPrevia.estado)}`}>
                        {empresaVistaPrevia.estado || "Sin estado"}
                      </span>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div><strong>Categoría:</strong> {empresaVistaPrevia.categoria || 'No especificada'}</div>
                    <div><strong>Dirección:</strong> {empresaVistaPrevia.direccion || 'No especificada'}</div>
                    <div><strong>Teléfono:</strong> {empresaVistaPrevia.telefono || 'No especificado'}</div>
                    <div><strong>Email:</strong> {empresaVistaPrevia.email || 'No especificado'}</div>
                    {empresaVistaPrevia.web && (
                      <div>
                        <strong>Sitio Web:</strong> 
                        <a 
                          href={empresaVistaPrevia.web.startsWith("http") ? empresaVistaPrevia.web : `https://${empresaVistaPrevia.web}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 ml-2 underline"
                        >
                          {empresaVistaPrevia.web}
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Información Adicional */}
                <div className="space-y-4">
                  {empresaVistaPrevia.descripcion && (
                    <div>
                      <strong>Descripción:</strong>
                      <p className="mt-1 text-gray-700">{empresaVistaPrevia.descripcion}</p>
                    </div>
                  )}
                  
                  {(empresaVistaPrevia.horarios || empresaVistaPrevia.horario_atencion) && (
                    <div>
                      <strong>Horarios:</strong>
                      <p className="mt-1 text-gray-700">
                        {typeof empresaVistaPrevia.horarios === 'string' 
                          ? empresaVistaPrevia.horarios 
                          : (empresaVistaPrevia.horario_atencion || 'No especificados')}
                      </p>
                    </div>
                  )}
                  
                  {empresaVistaPrevia.marcas && empresaVistaPrevia.marcas.length > 0 && (
                    <div>
                      <strong>Marcas:</strong>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {empresaVistaPrevia.marcas.map((marca, index) => (
                          <span key={index} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                            {marca}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Botones de Acción */}
              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => setEmpresaVistaPrevia(null)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cerrar
                </button>
                
                {/* Pasar a Premium */}
                {!esPremium(empresaVistaPrevia) && (
                  <button
                    onClick={() => {
                      pasarAPremium(empresaVistaPrevia);
                      setEmpresaVistaPrevia(null);
                    }}
                    disabled={pasarAPremiumLoading === empresaVistaPrevia.id}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      pasarAPremiumLoading === empresaVistaPrevia.id
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-amber-600 text-white hover:bg-amber-700'
                    }`}
                    title="Pasar esta empresa a membresía Premium"
                  >
                    {pasarAPremiumLoading === empresaVistaPrevia.id ? 'Procesando...' : '💎 Pasar a Premium'}
                  </button>
                )}
                
                {/* Botón para ver perfil público (como lo ven los clientes) */}
                <a
                  href={`/empresa/${empresaVistaPrevia.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                >
                  👤 Ver Perfil Público
                </a>
                
                {/* Botón para ver dashboard interno del proveedor */}
                <a
                  href={`/perfil-proveedor/${empresaVistaPrevia.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  🏢 Ver Dashboard Interno
                </a>
                
                {/* Botón para sitio web (solo si existe) */}
                {empresaVistaPrevia.web && (
                  <a
                    href={empresaVistaPrevia.web.startsWith("http") ? empresaVistaPrevia.web : `https://${empresaVistaPrevia.web}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    🌐 Página Web
                  </a>
                )}
                
                <button
                  onClick={() => {
                    const id = empresaVistaPrevia.id;
                    setEmpresaVistaPrevia(null);
                    window.open(`/admin/editar-empresa/${id}`, '_blank', 'noopener,noreferrer');
                  }}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                >
                  ✏️ Editar Empresa
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ContactVerificationModal
        empresa={contactVerifyEmpresa}
        isOpen={!!contactVerifyEmpresa}
        onClose={() => setContactVerifyEmpresa(null)}
        onSaved={() => {}}
      />
    </section>
  );
}
